import * as Sentry from '@sentry/vue'
import { ref } from 'vue'

import { generateId } from '@/services/idService'
import { toPlainData } from '@/utils/plainData'

// Log levels
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'success'

// Log entry interface
export interface LogEntry {
  id: string
  timestamp: number
  level: LogLevel
  category: string
  message: string
  data?: unknown
  // Optional metadata
  userId?: string
  sessionId?: string
  buildInfo?: string
}

// Log categories for organization
export const LOG_CATEGORIES = {
  SYNC: 'sync',
  API: 'api',
  UI: 'ui',
  AUTH: 'auth',
  DATABASE: 'database',
  FILE: 'file',
  MAP: 'map',
  GENERAL: 'general',
  OFFLINE: 'offline',
} as const

export type LogCategory = (typeof LOG_CATEGORIES)[keyof typeof LOG_CATEGORIES]

export interface LogOptions {
  /**
   * Log to the browser console only — the entry is kept out of the in-app log buffer
   * (shown in the Sync status dialog) and out of Sentry. For high-frequency transient
   * noise (e.g. map tile fetch failures) that would drown out actionable entries.
   */
  consoleOnly?: boolean
}

class LogService {
  private logs = ref<LogEntry[]>([])
  private maxLogEntries = 200 // Keep last 200 log entries
  private sessionId = generateId()

  constructor() {
    // Log service initialization
    this.log('info', LOG_CATEGORIES.GENERAL, 'Log service initialized', {
      sessionId: this.sessionId,
      maxEntries: this.maxLogEntries,
    })
  }

  /**
   * Add a log entry
   */
  log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: unknown,
    options?: LogOptions,
  ): void {
    const entry: LogEntry = {
      id: generateId(),
      timestamp: Date.now(),
      level,
      category,
      message,
      data,
      sessionId: this.sessionId,
    }

    if (options?.consoleOnly) {
      this.logToConsole(entry)
      return
    }

    // Add to beginning of array for most recent first
    this.logs.value.unshift(entry)

    // Keep only the most recent entries
    if (this.logs.value.length > this.maxLogEntries) {
      this.logs.value = this.logs.value.slice(0, this.maxLogEntries)
    }

    if (level === 'error') {
      this.captureError(category, message, data)
    }

    // Also log to console for development
    this.logToConsole(entry)
  }

  /**
   * Convenience methods for different log levels
   */
  debug(category: LogCategory, message: string, data?: unknown, options?: LogOptions): void {
    this.log('debug', category, message, data, options)
  }

  info(category: LogCategory, message: string, data?: unknown, options?: LogOptions): void {
    this.log('info', category, message, data, options)
  }

  warn(category: LogCategory, message: string, data?: unknown, options?: LogOptions): void {
    this.log('warn', category, message, data, options)
  }

  error(category: LogCategory, message: string, data?: unknown, options?: LogOptions): void {
    this.log('error', category, message, data, options)
  }

  success(category: LogCategory, message: string, data?: unknown, options?: LogOptions): void {
    this.log('success', category, message, data, options)
  }

  /**
   * Get all logs
   */
  getLogs(): LogEntry[] {
    return this.logs.value
  }

  /**
   * Get logs filtered by category
   */
  getLogsByCategory(category: LogCategory): LogEntry[] {
    return this.logs.value.filter((entry) => entry.category === category)
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.value.filter((entry) => entry.level === level)
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs.value = []
    this.info(LOG_CATEGORIES.GENERAL, 'Logs cleared')
  }

  /**
   * Export logs as text for copying/debugging
   */
  exportLogsAsText(maxEntries = 50): string {
    return this.logs.value
      .slice(0, maxEntries)
      .map((entry) => {
        const timestamp = new Date(entry.timestamp).toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
        const level = entry.level.toUpperCase().padEnd(7)
        const category = entry.category.toUpperCase().padEnd(8)
        const data = entry.data ? ` | ${JSON.stringify(entry.data)}` : ''
        return `${timestamp} ${level} [${category}] ${entry.message}${data}`
      })
      .join('\n')
  }

  /**
   * Log to browser console with appropriate method
   */
  private logToConsole(entry: LogEntry): void {
    const prefix = `[${entry.category.toUpperCase()}]`
    const message = `${prefix} ${entry.message}`

    switch (entry.level) {
      case 'debug':
        console.debug(message, entry.data)
        break
      case 'info':
        console.info(message, entry.data)
        break
      case 'warn':
        console.warn(message, entry.data)
        break
      case 'error':
        console.error(message, entry.data)
        break
      case 'success':
        console.log(message, entry.data)
        break
      default:
        console.log(message, entry.data)
    }
  }

  private captureError(category: LogCategory, message: string, data?: unknown): void {
    if (!import.meta.env.PROD) return

    const error = this.extractError(data)
    const contextData = data === undefined ? undefined : toPlainData(data)

    Sentry.withScope((scope) => {
      scope.setTag('log.category', category)
      scope.setExtra('log.message', message)

      if (contextData !== undefined) {
        scope.setContext('log.data', { payload: contextData })
      }

      if (error) {
        Sentry.captureException(error)
        return
      }

      Sentry.captureMessage(`[${category}] ${message}`, 'error')
    })
  }

  private extractError(data: unknown): Error | null {
    if (data instanceof Error) return data

    if (typeof data === 'object' && data !== null && 'error' in data) {
      const nestedError = (data as { error?: unknown }).error
      if (nestedError instanceof Error) {
        return nestedError
      }
    }

    return null
  }
}

// Create singleton instance
export const logService = new LogService()
